import type { OpFairView } from '@/lib/opOdds';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { makerRateForEntry } from '@/lib/pricing/fees';
import { theoreticalEntryRoi, theoreticalRoiText } from '@/lib/pricing/theoreticalRoi';
import { useFeeFor, useFeeDisplayStatus } from '@/services/hydraFees';
import { classifyLineAvailability } from '@/lib/sport/lineAvailability';
import type { VenueBook } from '../ValueDashboard/types';
import { bestPriceCentsForOrderSide } from '../ValueDashboard/tradeModalBook';

/** Display-only estimate. No order or sizing store is imported here. */
export function LineTheoreticalRoi({ fair, yesIsAway, strike, book, flipped = false }: {
  fair: OpFairView | null; yesIsAway: boolean; strike: number;
  book: VenueBook | null | undefined; flipped?: boolean;
}) {
  const nowMs = useSharedNow(1000);
  const entry = useFeeFor(book?.marketTicker);
  const feeStatus = useFeeDisplayStatus(book?.marketTicker, entry);
  const makerFeeRate = feeStatus === 'exact' ? makerRateForEntry(entry) : null;
  const slots = flipped ? ['no', 'yes'] as const : ['yes', 'no'] as const;
  return (
    <span className="flex h-8 flex-col whitespace-nowrap font-mono text-[10px] tabular-nums text-muted-foreground" data-theoretical-roi="">
      <span className="flex h-4 items-center justify-between gap-2">
      <span title="Conditional passive-fill return versus exact Pinnacle fair, including the estimated entry maker fee. Before rebates; not account P&L or a guaranteed fill.">Theo ROI · P bid/net</span>
      {slots.map(side => {
        // Match the existing maker pill/ladder's dust and last-trade sanity
        // rules. A raw maximum can select a level those surfaces reject.
        // Keep the estimate unavailable for malformed or bookless inputs;
        // scalar BBO fallback is not an observed passive book bid here.
        const validBook = book ? { ...book, [side]: book[side].filter(l =>
          Number.isFinite(l.size) && Number.isFinite(l.priceCents) && l.priceCents > 0 && l.priceCents < 100,
        ) } : undefined;
        const priceCents = bestPriceCentsForOrderSide(validBook, side) ?? null;
        const result = theoreticalEntryRoi({ fair, fairSide: (side === 'yes') === yesIsAway ? 'away' : 'home',
          strike, priceCents, makerFeeRate, nowMs, available: classifyLineAvailability(book) === 'active' });
        return <span key={side} data-roi-side={side} title={result.reason ?? `${side.toUpperCase()} passive fill at ${priceCents}¢; Pinnacle fair; entry maker fee included once; no rebate assumed`}>
          {side.toUpperCase()} {theoreticalRoiText(result)}
        </span>;
      })}
      </span>
      <span className="h-4" data-roi-source-time="">
        {fair?.anchorBookmaker === 'pinnacle' && fair.anchorUpdatedAtMs != null
          && Number.isFinite(fair.anchorUpdatedAtMs) && Math.abs(fair.anchorUpdatedAtMs) <= 8.64e15
          ? `Pinnacle ${new Date(fair.anchorUpdatedAtMs).toISOString().slice(0,23).replace('T',' ')} UTC`
          : 'Pinnacle time unavailable'}
      </span>
    </span>
  );
}
